import { GameConstants } from "../GameConstants";
import { GameVars } from "../GameVars";
import { Board } from "./Board";
import { SwitchButton } from "../splash/SwitchButton";
import { AudioManager } from "../AudioManager";
import { GameManager } from "../GameManager";
import { BoardManagerAI } from "./BoardManagerAI";
import { SponsorManager, WhyCallAd } from "../SponsorManager";
import { RectangleWithRadius } from "../utils/RectangleWithRadius";

export class PauseLayer extends Phaser.Group {

    constructor(game: Phaser.Game) {

        super(game, null, "pause-layer");

        const darkLayer = this.create(0, 0, this.game.cache.getBitmapData(GameConstants.DARK_BLUE_SQUARE));
        darkLayer.alpha = GameConstants.ALPHA_LAYERS;
        darkLayer.inputEnabled = true;
        darkLayer.events.onInputDown.add(this.onClickDarkLayer, this);
        darkLayer.scale.set(GameConstants.GAME_WIDTH / GameConstants.BITMAP_SIZE, GameConstants.GAME_HEIGHT / GameConstants.BITMAP_SIZE);

        const menuContainer = new Phaser.Group(this.game);
        menuContainer.scale.y = GameVars.scaleY;
        menuContainer.x = GameConstants.GAME_WIDTH / 2;
        menuContainer.y = Board.BOARD_PY * GameVars.scaleY;
        this.add(menuContainer);

        let w = 480;
        let h = 600;

        const rectangleWithRadius = new RectangleWithRadius(this.game, -w / 2, -h / 2, w, h, 6, "#1b2d3d", .85);
        menuContainer.add(rectangleWithRadius);

        const resignButton = new Phaser.Button(this.game, 0, -190, "texture_atlas_2_" + GameVars.language, this.onClickResign, this);
        resignButton.setFrames("button_resign_on.png", "button_resign_off.png", "button_resign_on.png");
        resignButton.forceOut = true;
        resignButton.anchor.set(.5);
        resignButton.name = GameConstants.DIFFICULTY_EASY;
        menuContainer.add(resignButton);

        const audioSwitchButton = new SwitchButton(this.game, !AudioManager.isMuted, "switch_on.png", "switch_off.png", this);
        audioSwitchButton.x = 20;
        audioSwitchButton.y = -100;
        audioSwitchButton.name = GameConstants.AUDIO_SWITCH;
        menuContainer.add(audioSwitchButton);

        const timerSwitchButton = new SwitchButton(this.game, GameVars.isTimerOn, "switch_on.png", "switch_off.png", this);
        timerSwitchButton.x = audioSwitchButton.x;
        timerSwitchButton.y = audioSwitchButton.y + 96;
        timerSwitchButton.name = GameConstants.TIMER_SWITCH;
        menuContainer.add(timerSwitchButton);

        const soundFXLabel = new Phaser.Text(this.game, -100, -92 , GameVars.texts[GameVars.language]["SOUND FX"], { font: "34px Roboto Slab Regular", fill: "#FFFFFF", align: "center"}); 
        soundFXLabel.anchor.x = .5;
        menuContainer.add(soundFXLabel);

        const timerLabel = new Phaser.Text(this.game, soundFXLabel.x, soundFXLabel.y + 96 , GameVars.texts[GameVars.language]["TIMER"], { font: "34px Roboto Slab Regular", fill: "#FFFFFF", align: "center"}); 
        timerLabel.anchor.x = .5;
        menuContainer.add(timerLabel);

        const homeButton = new Phaser.Button(this.game, 0, 230, "texture_atlas_2_" + GameVars.language, this.onClickHome, this);
        homeButton.setFrames("button_home_big_on.png", "button_home_big_off.png", "button_home_big_on.png");
        homeButton.forceOut = true;
        homeButton.anchor.set(.5);
        menuContainer.add(homeButton);

        const retryButton = new Phaser.Button(this.game, 0, 130, "texture_atlas_2_" + GameVars.language, this.onClickRetry, this);
        retryButton.setFrames("button_rematch_on.png", "button_rematch_off.png", "button_rematch_on.png");
        retryButton.forceOut = true;
        retryButton.anchor.set(.5);
        menuContainer.add(retryButton);

        AudioManager.setMusicVolume(1);
    }

    public switched(switchButton: SwitchButton): void {

        if (switchButton.name === GameConstants.AUDIO_SWITCH) {
            
            if (switchButton.isOn) {
                AudioManager.unmute();
            } else {
                AudioManager.mute();
            }

        } else {
            
            if (switchButton.isOn) {
                GameManager.changeTimerPreferences(true);
            } else {
                GameManager.changeTimerPreferences(false);
            }
        }
    }

    private onClickResign(b: Phaser.Button): void {

        b.clearFrames();

        if (GameVars.againstAI) {
            BoardManagerAI.resign();      
        }
        
        AudioManager.playSound("click"); 
    }

    private onClickHome(b: Phaser.Button): void {

        b.clearFrames();

        if (GameConstants.SPONSOR === GameConstants.GAME_DISTRIBUTION) {
            if (typeof gdsdk !== "undefined" && gdsdk.showAd !== "undefined") {
                gdsdk.showAd();
                GameVars.typeAd = "MainMenu";
            } else {
                BoardManagerAI.leaveRoom();
            }
        } else {
            SponsorManager.adFilter(() => {
                BoardManagerAI.leaveRoom();
            }, this.game, WhyCallAd.onClickHome);
        }

        SponsorManager.adFilter(() => {
            BoardManagerAI.leaveRoom();
        }, this.game, WhyCallAd.onClickHome);
        
        AudioManager.playSound("click"); 
    }

    private onClickRetry(b: Phaser.Button): void {

        b.clearFrames();

        if (GameConstants.SPONSOR === GameConstants.GAME_DISTRIBUTION) {
            if (typeof gdsdk !== "undefined" && gdsdk.showAd !== "undefined") {
                gdsdk.showAd();
                GameVars.typeAd = "Reset";
            } else {
                GameManager.rematch();
            }
        } else if (GameConstants.SPONSOR === GameConstants.SPONSOR_POKI) {
            if (!AudioManager.isMuted) {
                AudioManager.forceMute();
            }
            PokiSDK.commercialBreak().then(
                () => {
                    if (!AudioManager.isMuted) {
                        AudioManager.forceUnmute();
                    }
                    GameVars.onAd = false;
                    this.game.paused = false;
                    GameManager.rematch();
                }
            );
            GameVars.onAd = true;
            this.game.paused = true;
        } else {
            SponsorManager.adFilter(() => {
                GameManager.rematch();
            }, this.game, WhyCallAd.onClickRetry);
        }  
        
        AudioManager.playSound("click"); 
    }

    private onClickDarkLayer(): void {

        if (GameVars.resigning) {
            return;
        }
        
        if (GameVars.againstAI) {
            BoardManagerAI.exitPauseMenu();
        }

        AudioManager.playSound("click"); 

        AudioManager.setMusicVolume(.5);
    }
}
